import React, { useState, useEffect } from 'react';
import {
  Calendar,
  CalendarDays,
  MapPin,
  Clock,
  Users,
  User,
  UserPlus,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  X,
  Plus,
  Trash2,
  Pencil,
  Zap,
  Lightbulb,
  Network,
  Star,
  Award,
  Sparkles,
  GraduationCap,
  LifeBuoy
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getSummitEventDetails, addSummitRegistration, getUserSummitRegistration, updateSummitRegistration, deleteSummitRegistration } from '../services/summitService';
import { useSummit } from '../contexts/SummitContext';
import PortalBackground from '../components/PortalBackground';

// Shared form styles (visual only)
const inputClass = "w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent";
const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5";

// Accent tiles cycled across learning tracks
const TRACK_TILES = [
  'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
  'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
];

const BBASummit = () => {
  useTheme(); // theme handled via Tailwind dark: classes
  const { currentUser } = useAuth();
  const { userRegistration, updateRegistrationStatus, refreshRegistration } = useSummit();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [eventDetails, setEventDetails] = useState(null);
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [checkingRegistration, setCheckingRegistration] = useState(false);
  const [showEditMode, setShowEditMode] = useState(false);
  const [editingRegistrationData, setEditingRegistrationData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    guests: []
  });
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    guests: []
  });
  const [imageErrors, setImageErrors] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load event details on component mount
  useEffect(() => {
    const loadEventDetails = async () => {
      try {
        setLoading(true);
        const details = await getSummitEventDetails();
        setEventDetails(details);
      } catch (error) {
        console.error('Error loading event details:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEventDetails();
  }, []);

  // Check user registration status when component mounts or user changes
  useEffect(() => {
    const checkRegistration = async () => {
      if (currentUser?.email) {
        setCheckingRegistration(true);
        // The context will handle the registration check
        await refreshRegistration();
        setCheckingRegistration(false);
      }
    };
    checkRegistration();
  }, [currentUser, refreshRegistration]);

  // Populate form data when userRegistration changes
  useEffect(() => {
    if (userRegistration) {
      // Pre-populate form with user data if registered
      setEditingRegistrationData({
        firstName: userRegistration.firstName || '',
        lastName: userRegistration.lastName || '',
        title: userRegistration.title || '',
        email: userRegistration.email || userRegistration.userEmail || '',
        guests: userRegistration.guests || []
      });
    } else {
      // Pre-populate form with user data if not registered
      if (currentUser?.displayName) {
        const nameParts = currentUser.displayName.split(' ');
        setRegistrationData(prev => ({
          ...prev,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          email: currentUser.email || ''
        }));
      } else {
        setRegistrationData(prev => ({
          ...prev,
          email: currentUser.email || ''
        }));
      }
    }
  }, [userRegistration, currentUser]);

  // Helper function to format date range
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const startMonth = months[start.getMonth()];
    const endMonth = months[end.getMonth()];
    const startDay = start.getDate();
    const endDay = end.getDate();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }
  };

  const summitData = {
    title: eventDetails ? `BBA Annual Summit ${new Date(eventDetails.startDate).getFullYear()}` : "BBA Annual Summit 2024",
    subtitle: "Building Breakthroughs Together",
    date: eventDetails ? formatDateRange(eventDetails.startDate, eventDetails.endDate) : "September 16-19, 2024",
    location: eventDetails ? eventDetails.location : "Nashville Convention Center, Nashville, TN",
    venue: eventDetails ? eventDetails.location : "Nashville Convention Center, Nashville, TN",
    theme: "Innovation • Growth • Transformation"
  };

  const schedule = eventDetails ? eventDetails.schedule.map(day => ({
    day: day.day,
    events: day.events.map(event => {
      // Parse time from event string (e.g., "Opening Keynote (9:00 AM)" -> "9:00 AM")
      const timeMatch = event.match(/\(([^)]+)\)/);
      const time = timeMatch ? timeMatch[1] : "TBD";
      const title = event.replace(/\s*\([^)]*\)/, '');
      return {
        time,
        title,
        description: `Join us for ${title.toLowerCase()}`
      };
    })
  })) : [
    {
      day: "Day 1 - September 16",
      events: [
        { time: "8:00 AM", title: "Registration & Networking", description: "Check-in and connect with fellow industry leaders" },
        { time: "9:00 AM", title: "Opening Keynote", description: "The Future of Construction: Embracing Digital Transformation" },
        { time: "10:30 AM", title: "Breakout Sessions", description: "Choose from 6 specialized tracks" },
        { time: "12:00 PM", title: "Networking Lunch", description: "Connect with speakers and attendees" },
        { time: "2:00 PM", title: "Panel Discussion", description: "Navigating Market Challenges: Expert Insights" },
        { time: "4:00 PM", title: "Technology Showcase", description: "Latest innovations in construction tech" },
        { time: "6:00 PM", title: "Welcome Reception", description: "Cocktails and networking" }
      ]
    },
    {
      day: "Day 2 - September 17",
      events: [
        { time: "7:30 AM", title: "Breakfast & Networking", description: "Start your day with industry connections" },
        { time: "9:00 AM", title: "Morning Keynote", description: "Scaling Your Business: Strategies That Work" },
        { time: "10:30 AM", title: "Workshop Sessions", description: "Hands-on learning with industry experts" },
        { time: "12:00 PM", title: "Awards Luncheon", description: "Celebrating excellence in the industry" },
        { time: "2:00 PM", title: "Roundtable Discussions", description: "Peer-to-peer learning sessions" },
        { time: "4:00 PM", title: "Innovation Lab", description: "Interactive technology demonstrations" },
        { time: "6:30 PM", title: "Gala Dinner", description: "Formal networking and celebration" }
      ]
    },
    {
      day: "Day 3 - September 18",
      events: [
        { time: "8:00 AM", title: "Breakfast & Goal Setting", description: "Plan your next steps" },
        { time: "9:00 AM", title: "Closing Keynote", description: "Building Your Legacy: Long-term Success Strategies" },
        { time: "10:30 AM", title: "Action Planning Sessions", description: "Create your roadmap for success" },
        { time: "12:00 PM", title: "Farewell Lunch", description: "Final networking opportunities" },
        { time: "2:00 PM", title: "Optional Tours", description: "Visit local construction projects" }
      ]
    }
  ];

  const speakers = eventDetails ? eventDetails.speakers.map(speaker => ({
    name: speaker.name,
    title: speaker.title,
    company: speaker.company,
    topic: speaker.bio,
    image: speaker.image || "/speaker1.jpg"
  })) : [
    {
      name: "Dr. Sarah Johnson",
      title: "Business Strategy Expert",
      company: "Johnson Consulting Group",
      topic: "Leading authority on business scaling and strategic planning with 20+ years of experience.",
      image: "/speaker1.jpg"
    },
    {
      name: "Michael Chen",
      title: "Technology Integration Specialist",
      company: "TechBuild Solutions",
      topic: "Expert in digital transformation and automation for construction businesses.",
      image: "/speaker2.jpg"
    },
    {
      name: "Lisa Rodriguez",
      title: "Financial Management Advisor",
      company: "Rodriguez Financial Group",
      topic: "Specialist in financial optimization and growth strategies for small businesses.",
      image: "/speaker3.jpg"
    },
    {
      name: "David Thompson",
      title: "Leadership Development Coach",
      company: "Thompson Leadership Institute",
      topic: "Dedicated to building high-performing teams and developing leadership skills.",
      image: "/speaker4.jpg"
    }
  ];

  const tracks = eventDetails ? eventDetails.learningTracks.map((track, index) => {
    const icons = [
      <TrendingUp className="w-4 h-4" />,
      <Zap className="w-4 h-4" />,
      <Lightbulb className="w-4 h-4" />,
      <Network className="w-4 h-4" />
    ];
    return {
      title: track.name,
      icon: icons[index % icons.length],
      sessions: track.sessions
    };
  }) : [
    {
      title: "Business Strategy & Scaling",
      icon: <TrendingUp className="w-4 h-4" />,
      sessions: ["Strategic Planning Fundamentals", "Scaling Operations", "Market Expansion Strategies"]
    },
    {
      title: "Financial Management",
      icon: <Zap className="w-4 h-4" />,
      sessions: ["Financial Planning & Forecasting", "Cash Flow Optimization", "Investment Strategies"]
    },
    {
      title: "Technology & Automation",
      icon: <Lightbulb className="w-4 h-4" />,
      sessions: ["Digital Transformation", "Automation Implementation", "Technology ROI"]
    },
    {
      title: "Leadership & Team Building",
      icon: <Network className="w-4 h-4" />,
      sessions: ["Team Building and Leadership Development", "Communication Skills", "Change Management"]
    }
  ];

  const benefits = eventDetails ? [
    ...eventDetails.overview.knowledgeSkills.slice(0, 4),
    ...eventDetails.overview.resourcesSupport.slice(0, 4)
  ] : [
    "Advanced business strategy and scaling techniques",
    "Latest industry trends and market analysis",
    "Financial management and optimization strategies",
    "Team building and leadership development",
    "Exclusive access to BBA resource library",
    "One-on-one consulting sessions",
    "Networking events and peer groups",
    "Follow-up support and accountability"
  ];

  const handleAddGuest = () => {
    setRegistrationData(prev => ({
      ...prev,
      guests: [...prev.guests, { firstName: '', lastName: '', title: '', email: '' }]
    }));
  };

  const handleRemoveGuest = (index) => {
    setRegistrationData(prev => ({
      ...prev,
      guests: prev.guests.filter((_, i) => i !== index)
    }));
  };

  const handleGuestChange = (index, field, value) => {
    setRegistrationData(prev => ({
      ...prev,
      guests: prev.guests.map((guest, i) =>
        i === index ? { ...guest, [field]: value } : guest
      )
    }));
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();

    if (!currentUser || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Save to summit registrations collection
      const registrationId = await addSummitRegistration({
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        title: registrationData.title,
        email: registrationData.email,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        guests: registrationData.guests.filter(guest =>
          guest.firstName && guest.lastName && guest.email
        ),
        registrationDate: new Date().toISOString()
      });

      // Note: Registration data is now stored in summit_registrations collection
      // No need to duplicate in user document

      // Update context with the actual data structure from the database
      const newRegistration = {
        id: registrationId,
        email: currentUser.email,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        title: registrationData.title,
        guests: registrationData.guests.filter(guest =>
          guest.firstName && guest.lastName && guest.email
        ),
        registrationDate: new Date().toISOString()
      };
      updateRegistrationStatus(newRegistration);

      // Close popup and reset form
      setShowRegisterPopup(false);
      setRegistrationData({
        firstName: '',
        lastName: '',
        title: '',
        email: '',
        guests: []
      });


    } catch (error) {
      console.error('Error submitting registration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditRegistration = async (e) => {
    e.preventDefault();

    if (!currentUser || !userRegistration || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Update registration using the new service function
      const updatedRegistrationData = {
        firstName: editingRegistrationData.firstName,
        lastName: editingRegistrationData.lastName,
        title: editingRegistrationData.title,
        email: editingRegistrationData.email,
        userEmail: currentUser.email,
        userId: currentUser.uid,
        guests: editingRegistrationData.guests.filter(guest =>
          guest.firstName && guest.lastName && guest.email
        ),
        registrationDate: userRegistration.registrationDate
      };

      await updateSummitRegistration(userRegistration.id, updatedRegistrationData);

      // Update context
      const updatedRegistration = {
        ...userRegistration,
        firstName: editingRegistrationData.firstName,
        lastName: editingRegistrationData.lastName,
        title: editingRegistrationData.title,
        email: editingRegistrationData.email,
        guests: editingRegistrationData.guests.filter(guest =>
          guest.firstName && guest.lastName && guest.email
        )
      };
      updateRegistrationStatus(updatedRegistration);

      // Exit edit mode
      setShowEditMode(false);

    } catch (error) {
      console.error('Error updating registration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnregister = async () => {
    if (!currentUser || !userRegistration) {
      return;
    }

    if (!window.confirm('Are you sure you want to unregister from the summit? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete registration using the new service function
      await deleteSummitRegistration(userRegistration.id, currentUser.uid);

      // Update context
      updateRegistrationStatus(null);
      setShowRegisterPopup(false);
      setShowEditMode(false);

    } catch (error) {
      console.error('Error unregistering:', error);

    }
  };

  const handleEditGuestChange = (index, field, value) => {
    setEditingRegistrationData(prev => ({
      ...prev,
      guests: prev.guests.map((guest, i) =>
        i === index ? { ...guest, [field]: value } : guest
      )
    }));
  };

  const handleAddEditGuest = () => {
    setEditingRegistrationData(prev => ({
      ...prev,
      guests: [...prev.guests, { firstName: '', lastName: '', title: '', email: '' }]
    }));
  };

  const handleRemoveEditGuest = (index) => {
    setEditingRegistrationData(prev => ({
      ...prev,
      guests: prev.guests.filter((_, i) => i !== index)
    }));
  };

  const handleImageError = (speakerIndex) => {
    setImageErrors(prev => new Set([...prev, speakerIndex]));
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          {/* Hero skeleton */}
          <div className="rounded-2xl bg-gray-200 dark:bg-gray-800 animate-pulse h-72"></div>
          {/* Card skeletons */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 animate-pulse">
                <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 mx-auto mb-3"></div>
                <div className="h-3 w-2/3 mx-auto rounded bg-gray-100 dark:bg-gray-800 mb-2"></div>
                <div className="h-2.5 w-1/2 mx-auto rounded bg-gray-100 dark:bg-gray-800"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 dark:from-blue-900 dark:via-purple-900 dark:to-indigo-900">
        {/* Header Image Background */}
        {eventDetails?.headerImage && (
          <div className="absolute inset-0">
            <img
              src={eventDetails.headerImage}
              alt="Summit Header"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-black opacity-60"></div>
          </div>
        )}

        {/* Fallback gradient background */}
        {!eventDetails?.headerImage && (
          <div className="absolute inset-0 bg-black opacity-20"></div>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              {summitData.title}
            </h1>
            <p className="text-lg md:text-2xl text-blue-100 mb-6">
              {summitData.subtitle}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-3 mb-10">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-white/10 border border-white/20 text-white backdrop-blur-sm">
                <Calendar className="w-4 h-4" />
                {summitData.date}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-white/10 border border-white/20 text-white backdrop-blur-sm">
                <MapPin className="w-4 h-4" />
                {summitData.location}
              </span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 max-w-2xl mx-auto w-full">
              {checkingRegistration ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-white text-sm">Checking registration...</p>
                </div>
              ) : userRegistration ? (
                <div className="text-center">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-emerald-500 text-white shadow-sm mb-3">
                    <CheckCircle2 className="w-4 h-4" />
                    You're Registered
                  </span>
                  <div>
                    <button
                      onClick={() => setShowRegisterPopup(true)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-100 text-gray-900 rounded-lg text-sm font-semibold shadow-sm transition-colors"
                    >
                      View Registration Details
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (!currentUser) {
                      return;
                    }
                    setShowRegisterPopup(true);
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-red-50 text-red-600 rounded-lg text-lg font-semibold shadow-sm transition-colors"
                >
                  Register Now
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="sticky top-0 z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex justify-center gap-2 py-3 overflow-x-auto">
            {['overview', 'schedule', 'speakers', 'tracks'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-10">
            <div className="text-center">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Why Attend the BBA Summit?</h2>
              <p className="text-base text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {eventDetails?.overview?.whyAttend ||
                  "Join us for three days of intensive learning, networking, and inspiration. " +
                  "The BBA Annual Summit brings together the brightest minds in construction " +
                  "to share insights, strategies, and innovations that will transform your business."
                }
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Star, tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', title: "Expert Speakers", description: "Learn from the best in the industry" },
                { icon: Award, tile: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400', title: "Awards Ceremony", description: "Celebrate excellence and achievement" },
                { icon: Network, tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400', title: "Networking", description: "Build lasting business relationships" },
                { icon: TrendingUp, tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', title: "Growth Strategies", description: "Learn proven business scaling techniques" }
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 flex flex-col items-center text-center">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mb-3 ${feature.tile}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 md:p-8">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-6">
                <Sparkles className="w-4 h-4 text-red-600 dark:text-red-400" />
                What You'll Gain
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-3">
                    <GraduationCap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Knowledge & Skills
                  </h4>
                  <ul className="space-y-2.5">
                    {benefits.slice(0, 4).map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 mt-0.5 shrink-0" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-3">
                    <LifeBuoy className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    Resources & Support
                  </h4>
                  <ul className="space-y-2.5">
                    {benefits.slice(4).map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 mt-0.5 shrink-0" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Conference Schedule</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Three days of keynotes, workshops, and networking.</p>
            </div>
            {schedule.map((day, dayIndex) => (
              <div key={dayIndex} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center">
                    <CalendarDays className="w-4 h-4 text-red-600 dark:text-red-400" />
                    {day.day}
                  </h3>
                </div>
                <div className="p-6">
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {day.events.map((event, eventIndex) => (
                      <div key={eventIndex} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                        <span className="inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 w-24 whitespace-nowrap shrink-0">
                          <Clock className="w-3 h-3" />
                          {event.time}
                        </span>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{event.title}</h4>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Speakers Tab */}
        {activeTab === 'speakers' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Featured Speakers</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Industry leaders sharing what works.</p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {speakers.map((speaker, index) => (
                <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                  <div className="shrink-0">
                    {speaker.image && speaker.image !== "/speaker1.jpg" && !imageErrors.has(index) ? (
                      <img
                        src={speaker.image}
                        alt={speaker.name}
                        className="w-24 h-24 rounded-full object-cover ring-4 ring-gray-100 dark:ring-gray-800"
                        onError={() => handleImageError(index)}
                      />
                    ) : (
                      <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center ring-4 ring-gray-50 dark:ring-gray-800/50">
                        <Users className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{speaker.name}</h3>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mt-0.5">
                      {speaker.title}
                      {speaker.company && (
                        <span className="text-gray-500 dark:text-gray-400 font-normal"> · {speaker.company}</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{speaker.topic}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tracks Tab */}
        {activeTab === 'tracks' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Learning Tracks</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Pick the sessions that fit your goals.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {tracks.map((track, index) => (
                <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6">
                  <div className="flex flex-col items-center text-center gap-3 mb-4">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${TRACK_TILES[index % TRACK_TILES.length]}`}>
                      {track.icon}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{track.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {track.sessions.map((session, sessionIndex) => (
                      <li key={sessionIndex} className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 shrink-0" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{session}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}


      </div>

      {/* Registration Popup */}
      {showRegisterPopup && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowRegisterPopup(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-3xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
              <button
                onClick={() => setShowRegisterPopup(false)}
                className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                  {userRegistration ? <CalendarDays className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {userRegistration ? (showEditMode ? 'Edit Registration' : 'Registration Details') : 'Register for the Summit'}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {summitData.date} · {summitData.location}
                </p>
              </div>
            </div>

            {userRegistration && !showEditMode ? (
              // Show registration details
              <div className="p-6 space-y-6">
                <div className="text-center">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="w-3 h-3" />
                    Registration Confirmed
                  </span>
                </div>

                {/* Primary Registrant Details */}
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-3">
                    <User className="w-4 h-4 text-red-600 dark:text-red-400" />
                    Primary Registrant
                  </h3>
                  <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Name</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userRegistration.firstName} {userRegistration.lastName}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Title</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userRegistration.title}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Email</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userRegistration.email || userRegistration.userEmail}</p>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Guest Details */}
                {userRegistration.guests && userRegistration.guests.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-3">
                      <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      Additional Guests
                    </h3>
                    {userRegistration.guests.map((guest, index) => (
                      <div key={index} className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4 mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Guest {index + 1}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Name</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{guest.firstName} {guest.lastName}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Title</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{guest.title}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Email</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{guest.email}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t border-gray-100 dark:border-gray-800">
                  <button
                    onClick={handleUnregister}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Unregister
                  </button>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setShowEditMode(true)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit Registration
                    </button>
                    <button
                      onClick={() => setShowRegisterPopup(false)}
                      className="inline-flex items-center justify-center px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : userRegistration && showEditMode ? (
              // Show edit registration form
              <form onSubmit={handleEditRegistration} className="p-6 space-y-6">
                {/* Primary Registrant */}
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-5">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-4">
                    <User className="w-4 h-4 text-red-600 dark:text-red-400" />
                    Primary Registrant
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>First Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        value={editingRegistrationData.firstName}
                        onChange={(e) => setEditingRegistrationData(prev => ({ ...prev, firstName: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        value={editingRegistrationData.lastName}
                        onChange={(e) => setEditingRegistrationData(prev => ({ ...prev, lastName: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        value={editingRegistrationData.title}
                        onChange={(e) => setEditingRegistrationData(prev => ({ ...prev, title: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        required
                        value={editingRegistrationData.email}
                        onChange={(e) => setEditingRegistrationData(prev => ({ ...prev, email: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Guests */}
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-5">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-4">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Additional Guests
                  </h3>

                  {editingRegistrationData.guests.map((guest, index) => (
                    <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Guest {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => handleRemoveEditGuest(index)}
                          title="Remove guest"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>First Name</label>
                          <input
                            type="text"
                            value={guest.firstName}
                            onChange={(e) => handleEditGuestChange(index, 'firstName', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Last Name</label>
                          <input
                            type="text"
                            value={guest.lastName}
                            onChange={(e) => handleEditGuestChange(index, 'lastName', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Title</label>
                          <input
                            type="text"
                            value={guest.title}
                            onChange={(e) => handleEditGuestChange(index, 'title', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Email</label>
                          <input
                            type="text"
                            value={guest.email}
                            onChange={(e) => handleEditGuestChange(index, 'email', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddEditGuest}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-red-300 dark:hover:border-red-700 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Guest
                  </button>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-5 border-t border-gray-100 dark:border-gray-800">
                  <button
                    type="button"
                    onClick={() => setShowEditMode(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Saving...' : 'Update Registration'}
                  </button>
                </div>
              </form>
            ) : (
              // Show registration form
              <form onSubmit={handleSubmitRegistration} className="p-6 space-y-6">
                {/* Primary Registrant */}
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-5">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-4">
                    <User className="w-4 h-4 text-red-600 dark:text-red-400" />
                    Primary Registrant
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>First Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        value={registrationData.firstName}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, firstName: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        value={registrationData.lastName}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, lastName: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        value={registrationData.title}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, title: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        required
                        value={registrationData.email}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, email: e.target.value }))}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Guests */}
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-5">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-4">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Additional Guests
                  </h3>

                  {registrationData.guests.map((guest, index) => (
                    <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Guest {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => handleRemoveGuest(index)}
                          title="Remove guest"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>First Name</label>
                          <input
                            type="text"
                            value={guest.firstName}
                            onChange={(e) => handleGuestChange(index, 'firstName', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Last Name</label>
                          <input
                            type="text"
                            value={guest.lastName}
                            onChange={(e) => handleGuestChange(index, 'lastName', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Title</label>
                          <input
                            type="text"
                            value={guest.title}
                            onChange={(e) => handleGuestChange(index, 'title', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Email</label>
                          <input
                            type="text"
                            value={guest.email}
                            onChange={(e) => handleGuestChange(index, 'email', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddGuest}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-red-300 dark:hover:border-red-700 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Guest
                  </button>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-5 border-t border-gray-100 dark:border-gray-800">
                  <button
                    type="button"
                    onClick={() => setShowRegisterPopup(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BBASummit;